import React from 'react';
import { type ILabelActions } from '@cx-web/feature-i18n';

import ComplianceView from './ComplianceView';
import DoneView from './DoneView';
import ImageSection from './ImageSection';
import LoginView from './LoginView';
import styles from './MyOCLogin2018.module.css';
import ResetPasswordView from './ResetPasswordView';
import SignupView from './SignupView';
import { type AuthBanner, type LoginCredentials, type MyOCLogin2018View, type SignupCredentials } from './types';

export interface MyOCLogin2018ModalProps {
  complianceLoading?: boolean;
  /** Seeds the login/signup email field (auth-modal store `prefillEmail`). */
  defaultEmail?: string;
  doneJourney?: 'signup' | 'preferences';
  fromEmailVerification?: boolean;
  labelActions: ILabelActions;
  loginBanner?: AuthBanner | null;
  loginLoading?: boolean;
  onClose: () => void;
  onComplianceAccept: () => void;
  onDoneContinue: () => void;
  onForgotPassword: () => void;
  onLoginSubmit: (credentials: LoginCredentials) => void;
  onResetSubmit: (email: string) => void;
  onSignupSubmit: (credentials: SignupCredentials) => void;
  onSwitchToLogin: () => void;
  onSwitchToSignup: () => void;
  privacyPolicyHref?: string;
  resetLoading?: boolean;
  resetSubmitted?: boolean;
  /** Base URL the hero-panel artwork is served from (`${resourcesUrl}/assets/img/`). */
  resourcesUrl: string;
  signupBanner?: AuthBanner | null;
  signupLoading?: boolean;
  termsHref?: string;
  /** Active view, owned by the container's view state machine. */
  view: MyOCLogin2018View;
}

const renderView = (props: MyOCLogin2018ModalProps): React.ReactElement | null => {
  const { labelActions } = props;
  switch (props.view) {
    case 'login':
      return (
        <LoginView
          banner={props.loginBanner}
          defaultEmail={props.defaultEmail}
          fromEmailVerification={props.fromEmailVerification}
          labelActions={labelActions}
          loading={props.loginLoading}
          onForgotPassword={props.onForgotPassword}
          onSubmit={props.onLoginSubmit}
        />
      );
    case 'signup':
      return (
        <SignupView
          banner={props.signupBanner}
          defaultEmail={props.defaultEmail}
          labelActions={labelActions}
          loading={props.signupLoading}
          onSubmit={props.onSignupSubmit}
        />
      );
    case 'reset-password':
      return (
        <ResetPasswordView
          labelActions={labelActions}
          loading={props.resetLoading}
          onReturnToLogin={props.onSwitchToLogin}
          onSubmit={props.onResetSubmit}
          submitted={props.resetSubmitted}
        />
      );
    case 'done':
      return (
        <DoneView
          journey={props.doneJourney ?? 'signup'}
          labelActions={labelActions}
          onContinue={props.onDoneContinue}
        />
      );
    case 'compliance':
      return (
        <ComplianceView
          labelActions={labelActions}
          loading={props.complianceLoading}
          onAccept={props.onComplianceAccept}
          privacyPolicyHref={props.privacyPolicyHref ?? '#'}
          termsHref={props.termsHref ?? '#'}
        />
      );
    default:
      return null;
  }
};

const Footer = ({
  labelActions,
  onSwitchToLogin,
  onSwitchToSignup,
  view,
}: Pick<
  MyOCLogin2018ModalProps,
  'labelActions' | 'onSwitchToLogin' | 'onSwitchToSignup' | 'view'
>): React.ReactElement | null => {
  if (view === 'login') {
    return (
      <div className={styles.switcher}>
        <span>{labelActions.get('AUTH_NOT_A_MEMBER')}</span>
        <button className={styles.switcherButton} onClick={onSwitchToSignup} type="button">
          {labelActions.get('AUTH_SIGN_UP_NOW')}
        </button>
      </div>
    );
  }
  if (view === 'signup') {
    return (
      <div className={styles.switcher}>
        <span>{labelActions.get('AUTH_ALREADY_A_MEMBER')}</span>
        <button className={styles.switcherButton} onClick={onSwitchToLogin} type="button">
          {labelActions.get('AUTH_LOGIN')}
        </button>
      </div>
    );
  }
  return null;
};

/**
 * Modal frame for the ported MyOC auth widget: dimmed overlay + the legacy two-column card — hero
 * `ImageSection` on the left, the tilted form panel (green divider) on the right with a close control
 * and a login/signup switcher footer, rendering the active view. Below 600px the hero is hidden and the
 * form takes the full width. Overlay click and Escape both request close. Presentational — the
 * container owns view state and supplies all data/handlers.
 */
const MyOCLogin2018Modal: React.FC<MyOCLogin2018ModalProps> = props => {
  const { labelActions, onClose, onSwitchToLogin, onSwitchToSignup, resourcesUrl, view } = props;
  const dialogRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // Move focus into the dialog on open and restore it to the previously-focused element on close, so
  // keyboard and screen-reader users are placed inside the modal and returned to their origin when it
  // closes. Prefer the first form field (login/signup/reset) and fall back to the dialog container for
  // field-less views (compliance/done). Full Tab-cycle trapping is a deferred follow-up.
  React.useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const focusTarget = dialogRef.current?.querySelector<HTMLElement>('input, select, textarea') ?? dialogRef.current;
    focusTarget?.focus();
    return () => previouslyFocused?.focus?.();
  }, []);

  return (
    <div className={styles.myocLogin} data-testid="myoc-auth-modal">
      <div className={styles.overlay} onClick={onClose} />
      <div
        aria-modal="true"
        className={styles.container}
        ref={dialogRef}
        role="dialog"
        style={{ outline: 'none' }}
        tabIndex={-1}
      >
        <ImageSection labelActions={labelActions} resourcesUrl={resourcesUrl} view={view} />
        {/* Close lives on the container (not the form panel) so it pins to the modal's top-right corner
            in both layouts: dark over the white panel on desktop, white over the hero image on mobile
            where the form panel sits below the image. */}
        <button
          aria-label={labelActions.get('CLOSE')}
          className={styles.formClose}
          data-testid="myoc-auth-close"
          onClick={onClose}
          type="button"
        >
          &times;
        </button>
        <div className={styles.formTilt}>
          <div className={styles.formPanel}>
            {view === 'reset-password' && (
              <button
                aria-label="back"
                className={styles.formBack}
                data-testid="myoc-auth-back"
                onClick={onSwitchToLogin}
                type="button"
              >
                &larr;
              </button>
            )}
            <div className={styles.formContent}>{renderView(props)}</div>
            <Footer
              labelActions={labelActions}
              onSwitchToLogin={onSwitchToLogin}
              onSwitchToSignup={onSwitchToSignup}
              view={view}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyOCLogin2018Modal;
